import { isArray } from "lodash-es";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { cardSizeFrom } from "@/board/board-utils";
import { captureMessage } from "@/error/sentry";
import { relativeFromViewport } from "@/math/coordinate-utils";
import type { BoardType, Id } from "@/model/baseTypes";
import { boardTitle } from "@/model/baseTypes";
import type { Board, BoardData, BoardId, IterationStatus } from "@/model/board";
import { hasBoardObjectives, isFrontendBoard, isSolutionBoard } from "@/model/board";
import type { BoardCard, Card } from "@/model/card";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";
import type { FlexType } from "@/model/flexboard";
import type { Art } from "@/model/session";
import type { Shape } from "@/model/shape";
import type { StickyType } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";
import { asSemiReadonly } from "@/utils/vue";

import { useArtStore } from "./art";
import { useBoardStore } from "./board";
import { animateCardMove } from "./helpers/animator";
import type { BoardByTypeOptions } from "./helpers/boardByType";
import { findBoardByType, findBoardByTypeOptional } from "./helpers/boardByType";
import { flexBoard, objectivesBoard, objectivesBoardId } from "./helpers/frontendBoard";
import { mirrorableToNonFlexBoard } from "./helpers/mirror";
import { useIterationStore } from "./iteration";
import { useTeamStore } from "./team";
import { useUserStore } from "./user";

type BoardMap = Record<Board["id"], Board>;

const { tt } = useGlobalI18n();

export const useBoardsStore = defineStore(
  "boards",
  () => {
    const boards = ref<BoardMap>({ objectives: objectivesBoard() });

    function boardById(boardId: Board["id"]): Board {
      const board = boards.value[boardId];
      if (!board) {
        captureMessage("Could not find board", { info: { boardId } });
        return { id: boardId } as Board;
      }
      return board;
    }

    function boardByType<T extends BoardType>(type: T, options: BoardByTypeOptions = {}) {
      return findBoardByType(boards.value, type, options);
    }

    function boardByTypeOptional<T extends BoardType>(type: T, options: BoardByTypeOptions = {}) {
      return findBoardByTypeOptional(boards.value, type, options);
    }

    function stickyTypeOriginBoard(type: StickyType, pos: ViewportCoordinate) {
      // Exclude teamId if it belongs to an external team, as external teams do not have associated boards
      const rawTeamId = useBoardStore().positionalCardProperties(relativeFromViewport(pos)).teamId;
      const teamId = rawTeamId && !useTeamStore().isExternalTeam(rawTeamId) ? rawTeamId : undefined;
      return boardByType(type.origin, { teamId });
    }

    function artBoardsToLoad(force = false) {
      return [...currentArtBoards.value, ...solutionBoards.value]
        .filter((board) => {
          const wasLoaded = board.state.state !== "inited" && !force;
          return board !== useBoardStore().currentBoard() && !wasLoaded;
        })
        .sort((boardA, boardB) => order(boardA) - order(boardB));

      function order(board: Board) {
        const art = board.artId === useArtStore().current.id ? 0 : 5;
        const type = board.type === "backlog" ? 0 : board.type === "team" ? 1 : 2;
        return art + type;
      }
    }

    function artTeamBoards(artId?: Art["id"]) {
      const teamBoards = Object.values(boards.value).filter(
        (board): board is BoardData<"team"> => board.type === "team",
      );
      return teamBoards.filter((board) =>
        artId ? board.team.artId === artId : useArtStore().isCurrentArt(board.team.artId),
      );
    }

    const currentArtBoards = computed(() =>
      Object.values(boards.value).filter(
        (board) => (board.artId || "") === (useArtStore().current.id || ""),
      ),
    );

    const solutionBoards = computed(() =>
      Object.values(boards.value).filter((board) => isSolutionBoard(board.type)),
    );

    const backlogStickies = computed<BoardCard[]>(() =>
      Object.values({
        ...boardByType("backlog").cards,
        ...(hasSolutionBacklogBoard.value ? boardByType("solution_backlog").cards : {}),
      }),
    );

    const hasSolutionBacklogBoard = computed(() =>
      Object.values(boards.value).some((board) => board.type === "solution_backlog"),
    );

    function title(id?: string) {
      const board = id ? boardById(id) : useBoardStore().board;
      return board ? tt(boardTitle(board, useArtStore().artById(board.artId))) : "";
    }

    function boardTypeOrder(a: Board, b: Board) {
      const order: Record<string, number> = {
        solution_backlog: 0,
        solution: 1,
        backlog: 2,
        program: 3,
        objectives: 4,
        risk: 5,
        team: 6,
        flex: 7,
      };
      return (order[a.type] ?? 99) - (order[b.type] ?? 99);
    }

    function boardByOriginCard(card: Card): Board | undefined {
      const originBoardType = card.type.origin;
      const teamAndArt = { teamId: card.teamId || undefined, artId: card.artId || undefined };
      return boardByTypeOptional(originBoardType, teamAndArt);
    }

    function addBoard<T extends BoardType>(board: BoardData<T>): BoardData<T> {
      boards.value = { ...boards.value, [board.id]: board };
      return board;
    }

    function updateObjectiveBoardId() {
      Object.values(boards.value).forEach((board) => {
        if (board.type === "objectives") {
          board.id = objectivesBoardId();
        }
      });
    }

    function updateBoards<T extends BoardType>(
      type: T,
      newBoards: Array<BoardData<T>>,
      update: (exist: BoardData<T>, board: BoardData<T>) => void,
    ) {
      const bs = boards.value;
      for (const id in bs) {
        if (bs[id].type === type && !newBoards.find((board) => board.id === bs[id].id)) {
          delete boards.value[id];
        }
      }
      newBoards.forEach((board) => {
        const found = boards.value[board.id] as BoardData<T>;
        if (found) {
          update(found, board);
        } else {
          boards.value[board.id] = board;
        }
      });
    }

    function setVelocity(e: { id: string; velocity: number; iteration: number }) {
      const board = boards.value[e.id] as BoardData<"team"> | undefined;
      if (board) {
        useIterationStore().iteration(board, e.iteration).velocity = e.velocity;
      }
    }

    function setCardSize(e: BoardId & { factor: number }) {
      if (
        !useUserStore().isAllowed("edit") ||
        !useUserStore().isNonTeamZoomAllowed(useBoardStore().currentBoard())
      ) {
        return false;
      }

      if (!(e.boardId in boards.value)) {
        // Board is not present: it could be a flex board, which is not loaded
        // until it is visited for the first time. However, we could receive an
        // update from someone else on the board. Since the flex board content
        // will be loaded the first time we access it, it's safe to ignore the
        // event.
        return false;
      }
      boards.value[e.boardId].cardSize = cardSizeFrom(e);
      return true;
    }

    function setIterationStatus(e: {
      boardId?: string;
      id: number;
      status: IterationStatus;
      detail?: string;
    }) {
      const boardId = e.boardId || useBoardStore().currentBoard().id;
      const board = boards.value[boardId] as BoardData<"team"> | undefined;
      const iter = board?.iterationStates[e.id];
      if (iter) {
        iter.status = e.status;
        iter.detail = e.detail || null;
        if (e.status === "success") {
          setTimeout(() => setIterationStatus({ boardId, id: e.id, status: "synced" }), 5000);
        }
      }
    }

    function cardToFront(e: { id: string | string[] } & Partial<BoardId> & { zIndex?: number }) {
      const board = e.boardId
        ? boards.value[e.boardId]
        : useBoardStore().board && useBoardStore().currentBoard();
      if (!board) {
        // Board is not present: it could be a flex board, which is not loaded
        // until it is visited for the first time. However, we could receive an
        // update from someone else on the board. Since the flex board content
        // will be loaded the first time we access it, it's safe to ignore the
        // event.
        return false;
      }
      let zIndex = e.zIndex ?? board.maxZ + 1;
      let updated = false;
      const ids = isArray(e.id) ? e.id : [e.id];
      ids.forEach((id) => {
        const boardCard = board.cards[id];
        if (boardCard) {
          boardCard.meta.zIndex = zIndex++;
          updated = true;
        }
      });
      if (updated) {
        board.maxZ = Math.max(board.maxZ, zIndex - 1);
      }
      return updated;
    }

    function setCardPos(e: Id & BoardId & RelativeCoordinate, endFn?: () => void) {
      const boardCard = boards.value[e.boardId].cards[e.id];
      animateCardMove(
        boardCard,
        e,
        (pos) => {
          boardCard.meta.pos.x = pos.x;
          boardCard.meta.pos.y = pos.y;
        },
        endFn,
      );
    }

    function addShape(boardId: string, shape: Shape) {
      const { shapes, index } = findShape(boardId, shape.id);
      if (!shapes) {
        return shape;
      }
      if (index >= 0) {
        return shapes[index];
      }
      shapes.push(shape);
      return shape;
    }

    function editShape(boardId: string, shape: Shape) {
      const { shapes, index } = findShape(boardId, shape.id);
      if (shapes && index >= 0) {
        shapes[index] = shape;
      }
    }

    function removeShape(boardId: string, id: string) {
      const { shapes, index } = findShape(boardId, id);
      if (shapes && index >= 0) {
        shapes.splice(index, 1);
      }
    }

    function findShape(boardId: string, id: string) {
      const shapes = boards.value[boardId]?.shapes;
      if (!shapes) {
        captureMessage("Could not find board for shape", { info: { boardId } });
        return { shapes: undefined, index: -1 };
      }
      return { shapes, index: shapes.findIndex((s) => s.id === id) };
    }

    return {
      boards: asSemiReadonly(boards),

      boardById,
      boardByType,
      boardByTypeOptional,
      stickyTypeOriginBoard,
      artBoardsToLoad,
      artTeamBoards,
      currentArtBoards,
      solutionBoards,
      flexBoards: computed(() =>
        Object.values(boards.value).filter(
          (board): board is BoardData<"flex"> => board.type === "flex",
        ),
      ),
      boardsWithObjectives: computed(() => Object.values(boards.value).filter(hasBoardObjectives)),
      boardsInited: computed(() =>
        Object.values(boards.value).some((board) => !isFrontendBoard(board.type)),
      ),
      backlogStickies,
      hasSolutionBacklogBoard,
      hasSolutionBoard: computed(() =>
        Object.values(boards.value).some((board) => board.type === "solution"),
      ),
      mirrorTargetBoards: (cards: Card[]) =>
        Object.values(boards.value).filter(
          (board) => !isCurrentBoard(board) && mirrorableToNonFlexBoard(cards, board),
        ),
      title,
      boardTypeOrder,
      boardByOriginCard,

      addBoards: (newBoards: BoardMap) => (boards.value = { ...boards.value, ...newBoards }),
      addBoard,
      addFlexBoard: (id: string, name: string, flexType: FlexType) =>
        addBoard<"flex">(flexBoard(id, name, flexType)),
      removeFlexBoard: (id: string) => delete boards.value[id],
      updateObjectiveBoardId,
      updateBoards,
      setVelocity,
      setCardSize,
      setIterationStatus,
      cardToFront,
      setCardPos,
      addShape,
      editShape,
      removeShape,
      setBoards: (newBoards: BoardMap) => (boards.value = newBoards),
    };
  },
  { autoReset: true },
);

function isCurrentBoard(board: Board) {
  return board.id === useBoardStore().board?.id;
}
